import { ChangeEvent, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Link,
  Modal,
  ModalHeader,
  ModalContent,
  ModalBody,
  ModalFooter,
  Switch,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import _ from 'lodash';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

import { FormFieldSecure } from '@/components/layout/Elements';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import {
  useUnlinkAccountMutation,
  useUpdatePatientMutation,
} from '@/patient-app-common/api/patientApi';
import { Patient } from '@/patient-app-common/types/Patient';

import { SetTwoFA } from './SetTwoFa';

interface AccountProps {
  patient: Patient;
}

export function Account({ patient }: AccountProps) {
  const { email, emailNotification, phone, phoneNotification } = patient;

  return (
    <div>
      <ProfileEdit patient={patient} />
      <TwoStepVerification patient={patient} />
      <Notifications
        hasEmail={!_.isEmpty(email)}
        hasPhone={!_.isEmpty(phone)}
        emailNotification={emailNotification}
        phoneNotification={phoneNotification}
      />
      <Accessibility />
    </div>
  );
}

export function ProfileEdit({ patient }: { patient: Patient }) {
  const { t } = useTranslation('settings');
  const { requirePasswordLength13 } = useHospitalFlags();
  const [modal, setModal] = useState(false);

  const [updatePatient, { isLoading, isSuccess, isError }] =
    useUpdatePatientMutation();

  const [unlink] = useUnlinkAccountMutation();

  const [form, setForm] = useState({
    old: '',
    new: '',
    confirm: '',
  });

  const [errorFields, setErrorFields] = useState({
    old: '',
    new: '',
    confirm: '',
  });

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement>,
    field: 'old' | 'new' | 'confirm'
  ) => {
    setErrorFields({ old: '', new: '', confirm: '' });
    setForm({ ...form, [field]: e.target.value });
  };

  const validatePassword = () => {
    setErrorFields({ old: '', new: '', confirm: '' });

    const blankFields = [];

    for (const [key, value] of Object.entries(form)) {
      if (!value) {
        blankFields.push({ [key]: t('common.error.blank_input') });
      }
    }

    if (blankFields.length) {
      setErrorFields(_.merge({ ...errorFields }, ...blankFields));
      return false;
    }

    const hasNumber = /[0-9]/.test(form.new);
    const requiredLength = requirePasswordLength13 ? 13 : 8;
    const lengthError = `common.error.invalid_password_${requiredLength}`;

    if (form.new && (form.new.length < requiredLength || !hasNumber)) {
      setErrorFields({
        ...errorFields,
        new: t(lengthError),
      });
      return false;
    }

    if (form.new !== form.confirm) {
      setErrorFields({
        ...errorFields,
        confirm: t('common.error.confirm_password'),
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    const valid = validatePassword();
    if (!valid) return;

    try {
      const params = {
        oldPassword: form.old,
        password: form.new,
      };

      const result = await updatePatient(params).unwrap();
      if (!result.success) {
        setErrorFields({
          ...errorFields,
          old: t('profileedit.password.error.invalid'),
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  const unlinkOntarioTrust = async () => {
    await unlink({ integrationType: 'ontario_trusted_account' });
  };

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>{t('account.link.edit_password')}</ModalHeader>
          <ModalBody>
            <Alert
              color="danger"
              description={t('common.error.server')}
              hideIconWrapper
              isVisible={isError}
              variant="faded"
            />
            <Alert
              color="success"
              description={t('common.text.update_success')}
              hideIconWrapper
              isVisible={isSuccess && _.isEmpty(errorFields['old'])}
              variant="faded"
            />
            <FormFieldSecure
              color="primary"
              errorMessage={errorFields['old']}
              isInvalid={!_.isEmpty(errorFields['old'])}
              label={t('profileedit.input.current_password')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e, 'old')
              }
              variant="bordered"
            />
            <FormFieldSecure
              color="primary"
              errorMessage={errorFields['new']}
              isInvalid={!_.isEmpty(errorFields['new'])}
              label={t('profileedit.input.new_password')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e, 'new')
              }
              variant="bordered"
            />
            <FormFieldSecure
              color="primary"
              errorMessage={errorFields['confirm']}
              isInvalid={!_.isEmpty(errorFields['confirm'])}
              label={t('common.input.confirm_password')}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                handleInputChange(e, 'confirm')
              }
              variant="bordered"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              isLoading={isLoading}
              onPress={handleSubmit}
            >
              {t('common.action.submit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <div className="flex flex-col mt-4 space-y-4">
        <h2 className="font-semibold">{t('common.text.account')}</h2>
        {patient.integration?.ontarioTrustedAccount && (
          <Link
            anchorIcon={<Icon icon="bx:unlink" className="ml-4 text-lg" />}
            color="foreground"
            href="#"
            onPress={unlinkOntarioTrust}
            showAnchorIcon
            size="sm"
          >
            Unlink Ontario Trust Account
          </Link>
        )}
        <Link
          anchorIcon={<Icon icon="mdi:open-in-new" className="ml-4 text-lg" />}
          color="foreground"
          href="#"
          onPress={() => setModal(true)}
          showAnchorIcon
          size="sm"
        >
          {t('account.link.edit_password')}
        </Link>
      </div>
    </>
  );
}
interface NotificationProps {
  hasEmail: boolean;
  hasPhone: boolean;
  emailNotification: boolean;
  phoneNotification: boolean;
}

export function Notifications({
  hasEmail,
  hasPhone,
  emailNotification,
  phoneNotification,
}: NotificationProps) {
  const { t } = useTranslation('settings');
  const [updatePatient] = useUpdatePatientMutation();

  const handleSystemNotifications = async (
    value: boolean,
    type: 'email' | 'phone'
  ) => {
    let params = {};

    if (type === 'email') {
      params = { emailNotification: value };
      await updatePatient(params);
    }

    if (type === 'phone') {
      params = { phoneNotification: value };
    }

    try {
      await updatePatient(params);
    } catch (e) {
      console.log(e);
    }
  };

  if (!hasEmail && !hasPhone) return null;

  return (
    <div className="mt-8 space-y-4 flex flex-col">
      <h2 className="font-semibold">{t('account.section.notification')}</h2>
      {hasEmail && (
        <Switch
          size="sm"
          defaultSelected={emailNotification}
          color="primary"
          className="gap-8"
          onValueChange={(value) => handleSystemNotifications(value, 'email')}
        >
          {t('common.toggle.email_notification')}
        </Switch>
      )}
      {hasPhone && (
        <Switch
          size="sm"
          defaultSelected={phoneNotification}
          color="primary"
          className="gap-8"
          onValueChange={(value) => handleSystemNotifications(value, 'phone')}
        >
          {t('common.toggle.sms_notification')}
        </Switch>
      )}
    </div>
  );
}

export function Accessibility() {
  const { t } = useTranslation('settings');
  const { theme, setTheme } = useTheme();

  const [fontScale, setFontScale] = useState<null | number>(null);

  useEffect(() => {
    const savedFontScale = localStorage.getItem('fontScale') || 1;
    setFontScale(_.toNumber(savedFontScale));
    document.documentElement.style.fontSize = `${_.toNumber(savedFontScale) * 100}%`;
  }, []);

  useEffect(() => {
    if (!fontScale) return;
    localStorage.setItem('fontScale', _.toString(fontScale));
    document.documentElement.style.fontSize = `${fontScale * 100}%`;
  }, [fontScale]);

  const handleDarkToggle = (value: boolean) => {
    if (value) {
      setTheme('dark');
    } else {
      setTheme('light');
    }
  };

  return (
    <div className="mt-8 space-y-4">
      <h2 className="font-semibold">{t('common.text.accessibility')}</h2>
      <Switch
        size="sm"
        defaultSelected={theme === 'dark'}
        color="primary"
        className="gap-8"
        onValueChange={(value) => handleDarkToggle(value)}
      >
        {t('account.toggle.dark_mode')}
      </Switch>
    </div>
  );
}

export function TwoStepVerification({ patient }: { patient: Patient }) {
  const { t } = useTranslation('settings');
  const [modal, setModal] = useState(false);

  if (!patient.mfaType && !patient.mfaContact) return null;

  return (
    <>
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        scrollBehavior="inside"
      >
        <ModalContent>
          <SetTwoFA
            mfaType={patient.mfaType}
            handleExit={() => setModal(false)}
          />
        </ModalContent>
      </Modal>
      <Link
        anchorIcon={<Icon icon="mdi:open-in-new" className="ml-4 text-lg" />}
        className="mt-4"
        showAnchorIcon
        color="foreground"
        href="#"
        onPress={() => setModal(true)}
        size="sm"
      >
        {t('account.link.two_fa')}
      </Link>
    </>
  );
}
